import { useEffect, useRef, useState } from 'react';
import { Undo2, Eraser } from 'lucide-react';
import type { DamageMarkupValue, DamageMarkupStroke } from '@hcc/types';

interface DamageMarkupFieldProps {
  /** Background image (the vehicle diagram) URL or served key path. */
  imageKey: string;
  value: DamageMarkupValue;
  disabled?: boolean;
  /** Strokes changed (draw / undo / clear). */
  onChange?: (next: DamageMarkupValue) => void;
  /**
   * The flattened PNG (background + strokes) changed, or null when there are
   * no strokes. Also fired once after the image loads with restored strokes,
   * so a resumed draft can re-submit without redrawing.
   */
  onPng?: (png: string | null) => void;
}

const COLORS = ['#111111', '#dc2626', '#2563eb', '#d97706'];
/** Stroke width as a fraction of the image width. */
const STROKE_WIDTH = 0.005;

/**
 * Freehand markup over a fixed background image. Strokes are stored
 * normalised to 0..1 of the image so they re-render at any display size;
 * the flattened PNG is exported at the image's natural resolution.
 */
export function DamageMarkupField({
  imageKey,
  value,
  disabled,
  onChange,
  onPng,
}: DamageMarkupFieldProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [color, setColor] = useState(COLORS[0]!);
  const [imageReady, setImageReady] = useState(false);
  // The in-progress stroke lives in a ref: pointermove redraws directly
  // without a React state round-trip per point.
  const liveStroke = useRef<DamageMarkupStroke | null>(null);
  const regenerated = useRef(false);

  function drawStroke(
    ctx: CanvasRenderingContext2D,
    stroke: DamageMarkupStroke,
    w: number,
    h: number,
  ) {
    if (stroke.points.length < 4) {
      // A tap: draw a dot so a single click still marks the spot.
      const x = (stroke.points[0] ?? 0) * w;
      const y = (stroke.points[1] ?? 0) * h;
      ctx.fillStyle = stroke.color;
      ctx.beginPath();
      ctx.arc(x, y, Math.max(stroke.width * w, 1.5), 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = Math.max(stroke.width * w, 1.5);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(stroke.points[0]! * w, stroke.points[1]! * h);
    for (let i = 2; i < stroke.points.length; i += 2) {
      ctx.lineTo(stroke.points[i]! * w, stroke.points[i + 1]! * h);
    }
    ctx.stroke();
  }

  function redraw() {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const rect = img.getBoundingClientRect();
    if (rect.width === 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);
    for (const s of value.strokes) drawStroke(ctx, s, rect.width, rect.height);
    if (liveStroke.current) drawStroke(ctx, liveStroke.current, rect.width, rect.height);
  }

  /** Flatten background + strokes at the image's natural size. */
  function exportPng(strokes: DamageMarkupStroke[]): string | null {
    const img = imgRef.current;
    if (!img || strokes.length === 0) return null;
    const w = img.naturalWidth || 1000;
    const h = img.naturalHeight || 1000;
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = out.getContext('2d');
    if (!ctx) return null;
    try {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      for (const s of strokes) drawStroke(ctx, s, w, h);
      return out.toDataURL('image/png');
    } catch {
      // A cross-origin background taints the canvas; the strokes are still
      // stored in the value, only the flattened export is unavailable.
      return null;
    }
  }

  function commit(strokes: DamageMarkupStroke[]) {
    onChange?.({ strokes });
    onPng?.(exportPng(strokes));
  }

  // Redraw on stroke changes and container resizes.
  useEffect(() => {
    redraw();
    const img = imgRef.current;
    if (!img || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => redraw());
    ro.observe(img);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, imageReady]);

  // A resumed draft restores strokes from the autosaved value, but the
  // flattened PNG only exists in memory: regenerate it once so submit works
  // without touching the drawing.
  useEffect(() => {
    if (!imageReady || regenerated.current) return;
    regenerated.current = true;
    if (value.strokes.length > 0) onPng?.(exportPng(value.strokes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageReady]);

  function pointFromEvent(e: React.PointerEvent): [number, number] | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const x = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const y = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1);
    return [x, y];
  }

  function onPointerDown(e: React.PointerEvent) {
    if (disabled) return;
    const p = pointFromEvent(e);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    liveStroke.current = { color, width: STROKE_WIDTH, points: [...p] };
    redraw();
  }
  function onPointerMove(e: React.PointerEvent) {
    if (disabled || !liveStroke.current) return;
    const p = pointFromEvent(e);
    if (!p) return;
    liveStroke.current.points.push(...p);
    redraw();
  }
  function onPointerUp() {
    if (disabled || !liveStroke.current) return;
    const finished = liveStroke.current;
    liveStroke.current = null;
    commit([...value.strokes, finished]);
  }

  if (!imageKey) {
    return (
      <div className="rounded-token-sm border border-dashed border-line-strong/20 bg-surface-2 px-3 py-6 text-center text-xs text-fg-dim">
        No background image set for this markup field.
      </div>
    );
  }

  return (
    <div className="mt-1.5">
      {!disabled ? (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              aria-label={`Draw in ${c}`}
              aria-pressed={color === c}
              className={`h-7 w-7 rounded-full border-2 ${
                color === c ? 'border-accent ring-2 ring-ring/30' : 'border-line/20'
              }`}
              style={{ backgroundColor: c }}
            />
          ))}
          <span className="mx-1 h-5 w-px bg-line/20" aria-hidden />
          <button
            type="button"
            onClick={() => commit(value.strokes.slice(0, -1))}
            disabled={value.strokes.length === 0}
            className="inline-flex h-7 items-center gap-1 rounded-token-sm border border-line-strong/20 bg-surface px-2 text-xs text-fg-muted hover:bg-surface-2 disabled:opacity-40"
          >
            <Undo2 className="h-3.5 w-3.5" />
            Undo
          </button>
          <button
            type="button"
            onClick={() => commit([])}
            disabled={value.strokes.length === 0}
            className="inline-flex h-7 items-center gap-1 rounded-token-sm border border-line-strong/20 bg-surface px-2 text-xs text-fg-muted hover:bg-surface-2 disabled:opacity-40"
          >
            <Eraser className="h-3.5 w-3.5" />
            Clear
          </button>
        </div>
      ) : null}
      <div className="relative inline-block max-w-full overflow-hidden rounded-token-sm border border-line/10 bg-white">
        {/* No crossorigin attribute: the image route streams these bytes
            same-origin (never a 302 to S3), so the canvas stays untainted
            without needing bucket CORS. With crossorigin set, a redirecting
            deployment would fail the load outright; without it, the worst
            case is a tainted canvas, which exportPng already handles. */}
        <img
          ref={imgRef}
          src={imageKey}
          alt="Damage diagram"
          draggable={false}
          onLoad={() => setImageReady(true)}
          className="block max-w-full select-none"
        />
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className={`absolute inset-0 ${disabled ? 'pointer-events-none' : 'cursor-crosshair touch-none'}`}
        />
      </div>
    </div>
  );
}
